import React, { useEffect } from 'react';
import Modal from 'react-modal';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { TextVariant, Typography } from '@components/shared/design/Typography';
import CloseIcon from '@images/account/icon-x.svg';
import { useScrollBlock } from '@utils/useScrollBlock';
import PadSizeDescriptions from './PadSizeDescriptions';
import PadSizeGrid from './PadSizeGrid';

const customStyles = {
  content: {
    top: '0',
    left: '0',
    height: '100%',
    width: '100%',
    maxWidth: '600px',
    padding: '0',
    border: '0',
    borderRadius: '0',
    background: '#FFFBE7'
  },
  overlay: {
    background: `${Colors.cream}25`
  }
};

interface Props {
  modalOpen: boolean;
  closeModal: () => void;
}

const PadsCompareModal: React.FC<Props> = ({ modalOpen, closeModal }) => {
  const { t } = useTranslation();
  const [blockScroll, allowScroll] = useScrollBlock();

  useEffect(() => {
    if (modalOpen) {
      blockScroll();
    } else {
      allowScroll();
    }
  }, [modalOpen]);

  return (
    <Modal
      isOpen={modalOpen}
      onAfterOpen={() => null}
      onRequestClose={closeModal}
      style={customStyles}
      closeTimeoutMS={250}
      contentLabel={t('components.compare-pads-modal.title')}
    >
      <ModalBody>
        <ModalHeader>
          <Typography $variant={TextVariant.Heading6}>{t('components.compare-pads-modal.title')}</Typography>
          <CloseButton onClick={closeModal}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>
        <PadSizeGrid />
        <PadSizeDescriptions />
      </ModalBody>
    </Modal>
  );
};

export default PadsCompareModal;

const ModalHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;

  @media (max-width: 500px) {
    align-items: flex-end;
  }
`;

const ModalBody = styled.div`
  padding: 72px;
  text-align: center;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 32px;

  @media (max-width: 500px) {
    padding: 32px 24px;
  }
`;

const CloseButton = styled.button`
  background: ${Colors.paleGold25};
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 0;
  padding: 0;
  outline: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    svg {
      opacity: 0.5;
    }
  }

  svg {
    width: 50%;
    height: 50%;
  }
`;
